import { type NavigateFunction } from '@leagueplatform/routing';
import type { UseIntlReturn } from '@leagueplatform/locales';
import { type LiteralUnion, type Promisable } from 'type-fest';
import type { MasonryEngineMessagingController } from '../masonry-engine-messaging-controller';
import { type MasonryEngineStateController } from '../masonry-engine-state-controller';
import {
  type ActionResultData,
  type AnyMasonryEngineNodeAction,
  type MasonryEngineNodeAction,
  type MasonryEngineNodeAncestry,
} from './masonry-engine-node.types';
import { type MasonryEngineValidationControllerType } from './masonry-engine-validation.types';
import { type NarrowByType } from './util.types';

/**
 * Given a union of known actions and a string action type, will resolve to
 * the action type if it matches one of the known actions, or a wide-open
 * generic action type otherwise.
 */
export type ActionNarrowedByType<
  KnownActions extends MasonryEngineNodeAction,
  Type extends string,
> = Type extends KnownActions['type']
  ? NarrowByType<KnownActions, Type>
  : AnyMasonryEngineNodeAction<Type>;

/**
 * An action object with the addition of an optional browser `Event` (e.g.,
 * the `onClick` event associated with invoking this action), and an optional
 * MasonryEngineNodeAncestry containing the IDs of all ancestors of the invoking
 * node, plus optional origin context for onLoad-rooted chains.
 */
type InvokeActionFnActionParam<
  KnownActions extends MasonryEngineNodeAction,
  Type extends string,
> = (KnownActions | ActionNarrowedByType<KnownActions, Type>) & {
  event?: React.SyntheticEvent;
  currentTarget?: EventTarget | null;
  ancestry?: MasonryEngineNodeAncestry;
  origin?: 'onLoad';
};

export type InvokeActionFnResourcesParam = {
  stateController: MasonryEngineStateController;
  messagingController: MasonryEngineMessagingController;
  validationController: MasonryEngineValidationControllerType;
  navigate: NavigateFunction;
  intl: UseIntlReturn;
};

/**
 * The parameters reqired by action handlers (other than the action object itself).
 */
export type ActionHandlerResourceParam<
  KnownActions extends MasonryEngineNodeAction = MasonryEngineNodeAction,
> = InvokeActionFnResourcesParam & {
  invokeAction: InvokeActionFn<KnownActions, true>;
};

export class MasonryActionException extends Error {
  exception: unknown;

  constructor(public originalError?: unknown) {
    super('Masonry action exception');
    this.exception = originalError;
  }
}

export type MasonryActionResultSuccess<TData extends unknown = unknown> = {
  /** Indicates whether the action was successful */
  isSuccess: true;
  /** Optional data returned by the first action in the chain */
  data?: TData;
  /** Results from all subsequent chained actions (onSuccess/onFailure/onException), in execution order */
  chainedActionResults?: Array<MasonryActionResult | MasonryActionException>;
};

export type MasonryActionResultFailure = {
  /** Indicates whether the action was successful */
  isSuccess: false;
  /** Optional error returned by the action */
  error?: unknown;
  /** Results from all subsequent chained actions (onSuccess/onFailure/onException), in execution order */
  chainedActionResults?: Array<MasonryActionResult | MasonryActionException>;
};

/**
 * Standardized response object for all Masonry Engine actions.
 * All action handlers should return either this object directly or a Promise that resolves to this object.
 */
export type MasonryActionResult<TData extends unknown = unknown> =
  | MasonryActionResultSuccess<TData>
  | MasonryActionResultFailure;

/**
 * Flexible return type for action handlers - allows both meaningful responses and void for no-op handlers
 */
export type MasonryActionHandlerReturn<Action extends MasonryEngineNodeAction> =
  Promisable<MasonryActionResult<ActionResultData<Action>> | void>;

/**
 * A function for invoking an action. Takes the action object. The `InjectResources`
 * generic type parameter controls whether the function instance requires the
 * handler resources or if they are to be injected by the engine.
 */
export type InvokeActionFn<
  KnownActions extends MasonryEngineNodeAction = MasonryEngineNodeAction,
  InjectResources extends boolean = false,
> = <Type extends string>(
  action: InvokeActionFnActionParam<KnownActions, Type>,
  ...resources: InjectResources extends true
    ? []
    : [InvokeActionFnResourcesParam]
) => MasonryActionHandlerReturn<
  ActionNarrowedByType<KnownActions, Type>
> | void;

/**
 * The action object as it will be received by the handler function of the action
 * (same as {@link ActionWithEvent `ActionWithEvent`} but the `ancestry` property
 * is truthy, since it will be injected by the engine).
 */
export type InvokedAction<Action extends MasonryEngineNodeAction> =
  InvokeActionFnActionParam<Action, Action['type']> & {
    ancestry: MasonryEngineNodeAncestry;
  };

/**
 * A function that will be given a {@link InvokedAction `InvokedAction`}
 * that was just invoked, as well as the {@link MasonryEngineActionController
 * `MasonryEngineActionController`}'s action invoker, which can be used to invoke
 * more actions internally.
 */
export type MasonryEngineActionHandler<
  /**
   * The specific action meant to be handled by this handler.
   */
  ThisAction extends MasonryEngineNodeAction,
  /**
   * A union of other known actions assumed to be supported by the `MasonryEngineActionController`
   * in which this handler is registered.
   */
  KnownActions extends MasonryEngineNodeAction = MasonryEngineNodeAction,
> = (
  invokedAction: InvokedAction<ThisAction>,
  resources: ActionHandlerResourceParam<KnownActions>,
) => MasonryActionHandlerReturn<ThisAction> | void;

export type MasonryEngineActionHandlerMap<
  /**
   * A union of `MasonryEngineNodeAction`s. Ensures that all of them are represented
   * in the map, and that for each one, an appropriate handler is provided (e.g.
   * expecting the same payload).
   */
  KnownActions extends MasonryEngineNodeAction,
> = {
  [type in KnownActions['type']]: MasonryEngineActionHandler<
    NarrowByType<KnownActions, type>,
    KnownActions
  >;
};

/**
 * An enum with the event types expected by an event handler
 */
export enum MasonryEngineEvent {
  STARTED = 'started',
  ENDED = 'ended',
  ALL = 'all',
}

/**
 * A callback that will be called before a `MasonryEngineNodeAction` is fired,
 * after, or both.
 */
export type MasonryEngineEventListener<Action extends MasonryEngineNodeAction> =
  (
    invokedAction: InvokedAction<Action>,
    resolvedValue: Awaited<
      MasonryActionResult<ActionResultData<Action>>
    > | void,
  ) => void;

export type MasonryEngineActionController<
  /**
   * A union of known `MasonryEngineNodeAction` supported for this controller.
   */
  KnownActions extends MasonryEngineNodeAction,
> = {
  /**
   * @method `registerHandler` use to register action handlers per namespace.
   */
  registerHandler: <Type extends LiteralUnion<KnownActions['type'], string>>(
    type: Type,
    namespace: MasonryEngineNodeAction['namespace'],
    handler: MasonryEngineActionHandler<
      ActionNarrowedByType<KnownActions, Type>,
      KnownActions
    >,
  ) => void;

  /**
   * @method `registerUniversalHandler` registers an action handler that applies to all namespaces.
   * Unlike `registerHandler` which is namespace-specific, this handler will be invoked for the
   * specified action type regardless of namespace. Use `shouldSupersede` to control whether this
   * handler takes precedence over namespace-specific handlers (defaults to `true`).
   */
  registerUniversalHandler: <
    Type extends LiteralUnion<KnownActions['type'], string>,
  >(
    type: Type,
    handler: MasonryEngineActionHandler<
      ActionNarrowedByType<KnownActions, Type>,
      KnownActions
    >,
    shouldSupersede?: boolean,
  ) => void;

  /**
   * @method overrideHandler use by app developers to override single action handler for an App
   * To override default action for a driver, please use namespace "default"
   * Note: All action handler maps are driver specific.
   * */
  overrideHandler: MasonryEngineActionController<KnownActions>['registerHandler']; // <-- same API as `registerHandler`
  invokeAction: InvokeActionFn<KnownActions>;
  addEventListener: <
    Type extends LiteralUnion<KnownActions['type'], string>,
    Event extends MasonryEngineEvent,
  >(
    actionType: Type,
    namespace: string,
    eventType: Event,
    listener: MasonryEngineEventListener<
      ActionNarrowedByType<KnownActions, Type>
    >,
  ) => () => void;
  getRegisteredEventListeners: () => Record<string, number>;
};
